<template>
    <div class="field">
        <label class="field__title">
            <Translate translationKey="quests-dashboard.verificationCode"
                       fallback="Verification Code" />
        </label>
        <div class="verification-code">
            <input v-for="(_, index) in verificationCode"
                   :key="index"
                   ref="codeInputRefs"
                   v-model="verificationCode[index]"
                   class="field__input"
                   :class="{'field__input--error': backendError}"
                   maxlength="1"
                   @paste="handlePaste"
                   @input="filterInput($event, index); moveFocus(index)"
                   @keydown.backspace="handleBackspace($event, index)">
        </div>
        <div class="field__error-message">
            <span v-if="backendError">
                {{ backendError }}
            </span>
        </div>
    </div>
    <div class="code-resend">
        <div v-if="timer > 0"
             class="code-resend__verification code-resend__verification--cursor-regular">
            <Translate translationKey="quests-dashboard.resendIn"
                       :replacements="{seconds: timer}" />
        </div>
        <div v-else
             class="code-resend__verification link-hover-effect"
             @click="onResendCode">
            <Translate translationKey="quests-dashboard.resendVerification"
                       fallback="Resend verification code" />
        </div>
    </div>
</template>

<script setup lang="ts">
    import {
        ref, computed, nextTick, onMounted, watch,
    } from 'vue';

    defineProps<{
        backendError: string | null;
        timer: number;
    }>();

    const emit = defineEmits<{(e: 'submitCode', code: string): void;
                              (e: 'resendCode'): void;
                              (e: 'verificationSubmitAvailable', state: boolean): void;
    }>();

    const verificationCode = ref(['', '', '', '', '', '']);
    const codeInputRefs = ref<HTMLInputElement[]>([]);

    const allInputsFilled = computed(() => verificationCode.value.every((code) => code !== '' && code.match(/^[a-z0-9]$/i)));

    const moveFocus = (index: number) => {
        if (index < 5 && verificationCode.value[index]?.match(/^[a-z0-9]$/i)) {
            codeInputRefs.value[index + 1]?.focus();
        }
    };

    const handleBackspace = (e: KeyboardEvent, index: number) => {
        if (index > 0 && verificationCode.value[index] === '') {
            codeInputRefs.value[index - 1]?.focus();
        }
    };

    const submitCode = () => {
        if (allInputsFilled.value) {
            emit('submitCode', verificationCode.value.join('').toUpperCase());
        }
    };

    const filterInput = (e: Event, index: number) => {
        const input = e.target as HTMLInputElement;
        const value = input.value;

        // Allow only single alphanumeric character
        if (value && !value.match(/^[a-z0-9]$/i)) {
            verificationCode.value[index] = ''; // Clear invalid input
        } else if (value.length > 1) {
            // Handle case where user types quickly or pastes multi-char (though paste is handled separately)
            verificationCode.value[index] = value.charAt(0).match(/^[a-z0-9]$/i) ? value.charAt(0) : '';
        }

        submitCode();
    };

    const handlePaste = (event: ClipboardEvent) => {
        event.preventDefault();
        const pastedData = event.clipboardData?.getData('text').trim();
        if (!pastedData || pastedData.length !== 6 || !pastedData.match(/^[a-z0-9]{6}$/i)) {
            return;
        }

        const pastedChars = Array.from(pastedData);
        for (let i = 0; i < verificationCode.value.length; i++) {
            verificationCode.value[i] = pastedChars[i];
        }

        // Move focus to the last input after paste
        nextTick(() => {
            codeInputRefs.value[5]?.focus();
        });

        submitCode();
    };

    const onResendCode = () => {
        emit('resendCode');
        verificationCode.value = ['', '', '', '', '', ''];

        nextTick(() => {
            codeInputRefs.value[0]?.focus();
        });
    };

    watch(() => allInputsFilled.value, (newValue) => {
        emit('verificationSubmitAvailable', newValue);
    });

    onMounted(() => {
        nextTick(() => {
            codeInputRefs.value[0]?.focus();
        });
    });
</script>

<style scoped lang="scss">
@use "@/sass/partials/breakpoints" as breakpoints;
@use "@/sass/design-system/variables" as variables;
@use "@/js/quests/dashboard/sass/mixins/_modal" as modalMixins;
@use "@/js/quests/global/sass/mixins/link-hover-effect" as linkHoverEffectMixins;

.field {
    @include modalMixins.modal-field;

    & {
        width: 100%;
        text-align: center;
    }

    &__input {
        width: 50px;
        height: 50px;
        font-size: 18px;
        text-align: center;
        text-transform: uppercase;

        @media (max-width: breakpoints.$md-breakpoint-max-width) {
            width: 45px;
            height: 45px;
        }

        @media (max-width: 400px) {
            width: 35px;
            height: 35px;
        }

        &:hover {
            border: 1px solid variables.$blue600;
        }
    }

    &__error-message {
        text-align: center;
    }
}

.verification-code {
    display: flex;
    justify-content: center;
    gap: 8px;
}

.code-resend {
    display: flex;
    align-items: center;
    justify-content: center;

    &__verification {
        &.link-hover-effect {
            @include linkHoverEffectMixins.link-hover-effect(variables.$gray600);
        }

        color: variables.$gray600;
        cursor: pointer;
        font-size: 13px;

        &--cursor-regular {
            cursor: auto;
        }
    }
}
</style>
